import { create } from 'zustand'

import { usPhoneDigits } from '#/shared/lib/format'

import type { ChatPhoto } from './conversation'

/** Optimistic delivery state, mirroring the bubble's check marks: `sending`
 *  (no check) → `sent` (✓) → `delivered` (✓✓), or `failed` (the "Failed to
 *  send" notice). Advanced by `sms_status`. */
type OutboxStatus = 'sending' | 'sent' | 'delivered' | 'failed'

/** Monotonic rank so an out-of-order event never downgrades a bubble. */
const OUTBOX_RANK: Record<OutboxStatus, number> = {
  sending: 0,
  sent: 1,
  delivered: 2,
  failed: 3,
}

/** A settled bubble: its delivery outcome is final and can't advance further. */
function isSettled(status: OutboxStatus): boolean {
  return status === 'delivered' || status === 'failed'
}

/**
 * An outgoing message overlaid on the open thread's server timeline: shown
 * optimistically the instant the user sends, before the server confirms it.
 * Inbound messages come from the server refetch, not here.
 */
export interface OutboxMessage {
  id: string
  text: string
  photos: ChatPhoto[]
  status: OutboxStatus
  /** ISO timestamp captured when the user hit send. */
  time: string
  /** Sender line revealed under the bubble when tapped, e.g. "Jane Doe ·
   *  (415) 404-9036" — captured at send time so the optimistic bubble matches
   *  the server copy it shadows (the merge hides the echo, not the bubble). */
  route?: string
  /** True when sent on the shared mainline — gates the sender caption under
   *  media-only bubbles (DEV-700). */
  mainline?: boolean
  /** Failure reason, revealed in the failed "!" marker's popover. */
  error?: string
  /** True for a backend-rejected send (e.g. the contact opted out): it was
   *  never queued, so no server copy exists — kept when the thread closes and
   *  excluded from the overlay merge's echo dedupe. */
  noResend?: boolean
}

interface AddInput {
  id: string
  text: string
  files: ReadonlyArray<File>
  time: string
  route?: string
  /** Viewer caption for the send's photos — sender line + time (DEV-700),
   *  captured at send time like `route`. */
  photoCaption?: string
  /** True when sent on the shared mainline. */
  mainline?: boolean
}

interface OutboxState {
  /** Optimistic sends keyed by the open thread (counterparty number). */
  byThread: Record<string, OutboxMessage[]>
  add: (key: string, input: AddInput) => void
  markSent: (key: string, id: string) => void
  /** Flip a bubble to `failed` in place, with its reason — the message stays
   *  visible in the thread instead of being undone. */
  markFailed: (
    key: string,
    id: string,
    input: { error?: string; noResend?: boolean },
  ) => void
  /** Advance the oldest still-pending bubble on a thread to a delivery state,
   *  matched by counterparty number (the `sms_status` event carries no id we
   *  hold). Never downgrades — see OUTBOX_RANK. `error` (from a `failed`
   *  event) lands on the bubble for its "!" popover. */
  advanceDelivery: (phone: string, status: OutboxStatus, error?: string) => void
  remove: (key: string, id: string) => void
  clear: (key: string) => void
}

/** Local image previews so the optimistic bubble shows pictures immediately. */
function toPhotos(files: ReadonlyArray<File>, caption?: string): ChatPhoto[] {
  return files
    .filter((file) => file.type.startsWith('image/'))
    .map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
      caption,
    }))
}

function revokeAll(messages: OutboxMessage[] | undefined) {
  for (const message of messages ?? []) {
    for (const photo of message.photos) URL.revokeObjectURL(photo.url)
  }
}

/** Advance one bubble to `sent`, unless it already advanced further. */
function bubbleSent(list: OutboxMessage[], id: string): OutboxMessage[] {
  return list.map(
    (message): OutboxMessage =>
      message.id === id && OUTBOX_RANK[message.status] < OUTBOX_RANK.sent
        ? { ...message, status: 'sent' }
        : message,
  )
}

/** Flip one bubble to `failed` in place with its reason. An `sms_status:
 *  failed` socket event may have settled the bubble first (racing ahead of the
 *  HTTP response, which carries the richer reason) — so an already-failed
 *  bubble still takes the reason; only `delivered` is off-limits. */
function failBubble(
  list: OutboxMessage[],
  id: string,
  input: { error?: string; noResend?: boolean },
): OutboxMessage[] {
  return list.map((message): OutboxMessage => {
    if (message.id !== id || message.status === 'delivered') return message
    return {
      ...message,
      status: 'failed',
      error: input.error ?? message.error,
      noResend: input.noResend ?? message.noResend,
    }
  })
}

/** Bubbles that survive the thread closing: rejected sends (`noResend`) were
 *  never queued, so no server copy exists to reload them from — clearing them
 *  would erase the only record that the message failed. */
function keptOnClear(list: OutboxMessage[]): OutboxMessage[] {
  return list.filter(
    (message) => message.status === 'failed' && message.noResend,
  )
}

/**
 * Optimistic send queue. Messages land here the instant the user hits send —
 * the thread renders them right away — then flip to `sent` on success, or to
 * `failed` in place when the request errors or the backend rejects it (the
 * bubble stays in the thread, iMessage-style). A later carrier rejection
 * (`sms_status: failed`) flips the bubble to `failed` the same way so the
 * notice shows immediately. Object URLs are revoked when a message leaves.
 */
const useOutboxStore = create<OutboxState>((set) => ({
  byThread: {},
  add: (key, input) =>
    set((state) => {
      const message: OutboxMessage = {
        id: input.id,
        text: input.text,
        photos: toPhotos(input.files, input.photoCaption),
        status: 'sending',
        time: input.time,
        route: input.route,
        mainline: input.mainline,
      }
      return {
        byThread: {
          ...state.byThread,
          [key]: [...(state.byThread[key] ?? []), message],
        },
      }
    }),
  markSent: (key, id) =>
    set((state) => ({
      byThread: {
        ...state.byThread,
        [key]: bubbleSent(state.byThread[key] ?? [], id),
      },
    })),
  markFailed: (key, id, input) =>
    set((state) => ({
      byThread: {
        ...state.byThread,
        [key]: failBubble(state.byThread[key] ?? [], id, input),
      },
    })),
  advanceDelivery: (phone, status, error) =>
    set((state) => {
      const wanted = usPhoneDigits(phone)
      const target = OUTBOX_RANK[status]
      const byThread = { ...state.byThread }
      let changed = false
      for (const [key, list] of Object.entries(state.byThread)) {
        if (usPhoneDigits(key) !== wanted) continue
        // Settled bubbles are skipped even for a higher-ranked `failed` event:
        // a delivered message can't fail, so the failure must belong to a
        // younger still-pending send on the same thread.
        const index = list.findIndex(
          (message) =>
            !isSettled(message.status) && OUTBOX_RANK[message.status] < target,
        )
        if (index === -1) continue
        const next = [...list]
        next[index] = {
          ...next[index],
          status,
          error: error ?? next[index].error,
        }
        byThread[key] = next
        changed = true
      }
      return changed ? { byThread } : state
    }),
  remove: (key, id) =>
    set((state) => {
      const list = state.byThread[key] ?? []
      const target = list.find((message) => message.id === id)
      for (const photo of target?.photos ?? []) URL.revokeObjectURL(photo.url)
      return {
        byThread: {
          ...state.byThread,
          [key]: list.filter((message) => message.id !== id),
        },
      }
    }),
  clear: (key) =>
    set((state) => {
      const list = state.byThread[key] ?? []
      const kept = keptOnClear(list)
      revokeAll(list.filter((message) => !kept.includes(message)))
      const byThread = { ...state.byThread }
      if (kept.length > 0) byThread[key] = kept
      else delete byThread[key]
      return { byThread }
    }),
}))

/** Advance a thread's oldest pending optimistic bubble (sent → delivered, or
 *  failed, with the event's failure reason) from outside React — called by the
 *  `sms_status` socket handler. */
export function advanceOutboxDelivery(
  phone: string,
  status: 'sent' | 'delivered' | 'failed',
  error?: string,
): void {
  useOutboxStore.getState().advanceDelivery(phone, status, error)
}

/**
 * True when a thread keyed by this counterparty number holds an optimistic
 * bubble whose send is still in flight (not yet delivered) — i.e. this tab is
 * mid-send and already shows the message. The `sms_status` handler uses this to
 * skip refetching the thread for a locally-sent message (the overlay advances
 * its own check marks); only a message sent elsewhere needs a pull. Settled
 * (delivered/failed) bubbles don't count: they'd otherwise mask another tab's
 * sends to the same thread for the whole session. Read from outside React. */
export function hasPendingOutboxFor(phone: string): boolean {
  const wanted = usPhoneDigits(phone)
  return Object.entries(useOutboxStore.getState().byThread).some(
    ([key, list]) =>
      usPhoneDigits(key) === wanted &&
      list.some((message) => !isSettled(message.status)),
  )
}

const EMPTY: OutboxMessage[] = []

/** Outbox messages + actions bound to one thread (by counterparty number). */
export function useOutbox(key: string) {
  const messages = useOutboxStore((state) => state.byThread[key] ?? EMPTY)
  const add = useOutboxStore((state) => state.add)
  const markSent = useOutboxStore((state) => state.markSent)
  const markFailed = useOutboxStore((state) => state.markFailed)
  const remove = useOutboxStore((state) => state.remove)
  const clear = useOutboxStore((state) => state.clear)
  return { messages, add, markSent, markFailed, remove, clear }
}
